import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import { useGetTrackNTraceReceiveAsnQuery } from "../../../redux/services/trackntraceApiSlice";
import { removeEmptyKeys } from "../../../utils";
import { UICalendar } from "../../common/uiCalendar";
import "./style.css";
import { Page } from "../../../routes/config";
import { buildReceiveReportRows, type ReceiveAsnRow } from "./asnReportHelpers";
import {
  extendTrail,
  makeTrailItem,
  writeReportTrailState,
} from "./reportTrail";
import { exportRowsToWorkbook } from "./exportWorkbook";
import { ReportToolbar, ReportFilterChips } from "../../common/reportToolbar";
import Input from "../../common/input/Input";
import { UIAgGridColumn, UIAgGridTable } from "../../common/agGridTable";
import { Pagination } from "../../common/pagination";
import { MetricCard } from "./components/MetricCard";
import { AlertTriangle, BarcodeIcon, Layers, MapPin, PackageCheck, PackageOpen, PackageX, X } from "lucide-react";
import { MetricSkeleton } from "./components/MetricSkeleton";
import ReceiveList from "./components/ReceiveList";

const ReceiveReport = () => {
  const tenantId = useSelector(getTenantId);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAsn, setSelectedAsn] = useState<any>(null);

  const [asn, setAsn] = useState("");
  const [dateRange, setDateRange] = useState<moment.Moment[]>([]);
  const [appliedAsn, setAppliedAsn] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState<moment.Moment[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState<{
    field?: keyof ReceiveAsnRow;
    order?: "ascend" | "descend";
  }>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const ALL_COLUMNS = useMemo<UIAgGridColumn<ReceiveAsnRow>[]>(() => {
  return [
    {
      key: "asn",
      title: "ASN",
      dataIndex: "asn",
      minWidth: 140,
      flex: 1,
      sorter: true,
      render: (value: unknown) => (value ? String(value) : "-"),
    },
    {
      key: "expectedQty",
      title: "Expected QTY",
      dataIndex: "expectedQty",
      minWidth: 140,
      width: 140,
      sorter: true,
      render: (value: unknown) => (
        <div className="text-center w-full">
          {String(value ?? "0")}
        </div>
      ),
    },
    {
      key: "receivedQty",
      title: "Received QTY",
      dataIndex: "receivedQty",
      minWidth: 140,
      width: 140,
      sorter: true,
      render: (value: unknown) => (
        <div className="text-center w-full">
          {String(value ?? "0")}
        </div>
      ),
    },
    {
      key: "missingQty",
      title: "Missing QTY",
      dataIndex: "missingQty",
      minWidth: 140,
      width: 140,
      sorter: true,
      render: (value: unknown) => (
        <div className="text-center w-full">
          {String(value ?? "0")}
        </div>
      ),
    },
    {
      key: "unexpectedQty",
      title: "Unexpected QTY",
      dataIndex: "unexpectedQty",
      minWidth: 150,
      width: 150,
      sorter: true,
      render: (value: unknown) => (
        <div className="text-center w-full">
          {String(value ?? "0")}
        </div>
      ),
    },
    {
      key: "dateTime",
      title: "Date & Time",
      dataIndex: "dateTime",
      minWidth: 180,
      flex: 1,
      sorter: true,
      render: (value: unknown) =>
        value && moment(String(value)).isValid()
          ? moment(String(value)).format("DD MMM YY hh:mm A")
          : "-",
    },
  ];
}, []);
  const [columns, setColumns] = useState(ALL_COLUMNS);

  const receiveQueryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        reportType: "receive",
        page: 1,
        limit: 10000,
        from: appliedDateRange?.length
          ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD")
          : undefined,
        to: appliedDateRange?.length
          ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD")
          : undefined,
      }),
    [tenantId, appliedDateRange],
  );

  const { data: receiveData, isFetching } = useGetTrackNTraceReceiveAsnQuery(
    receiveQueryParams,
    {
      skip: !tenantId,
    },
  );

  const receiveResult = useMemo(
    () => receiveData?.result ?? receiveData,
    [receiveData],
  );
  const groupedRows = useMemo(
    () => buildReceiveReportRows(receiveResult),
    [receiveResult],
  );
  const tableRows = useMemo(() => {
    const q = appliedAsn.trim().toLowerCase();
    if (!q) return groupedRows;
    return groupedRows.filter((r) => r.asn.toLowerCase().includes(q));
  }, [groupedRows, appliedAsn]);

  const metrics = useMemo(() => {
  return tableRows.reduce(
    (acc, row) => {
      acc.expectedQty += Number(row.expectedQty || 0);
      acc.receivedQty += Number(row.receivedQty || 0);
      acc.missingQty += Number(row.missingQty || 0);
      acc.unexpectedQty += Number(row.unexpectedQty || 0);
      return acc;
    },
    {
      expectedQty: 0,
      receivedQty: 0,
      missingQty: 0,
      unexpectedQty: 0,
    }
  );
}, [tableRows]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return tableRows;
    const dir = sortConfig.order === "ascend" ? 1 : -1;
    const field = sortConfig.field;
    const numberFields: Array<keyof ReceiveAsnRow> = [
      "expectedQty",
      "receivedQty",
      "missingQty",
      "unexpectedQty",
    ];
    return [...tableRows].sort((a, b) => {
      if (field === "dateTime") {
        return (
          (moment(a.dateTime).valueOf() - moment(b.dateTime).valueOf()) * dir
        );
      }
      if (numberFields.includes(field)) {
        const aNum = Number(a[field] ?? 0);
        const bNum = Number(b[field] ?? 0);
        return (aNum - bNum) * dir;
      }
      return String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * dir;
    });
  }, [tableRows, sortConfig]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const totalCount = tableRows.length;

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ReceiveAsnRow> | SorterResult<ReceiveAsnRow>[],
  ) => {
    setPage(newPagination.current ?? 1);
    setPageSize(newPagination.pageSize ?? 50);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortConfig({
      field: (s?.field as keyof ReceiveAsnRow | undefined) ?? undefined,
      order: (s?.order as "ascend" | "descend" | undefined) ?? undefined,
    });
  };

  const applyFilters = () => {
    setAppliedAsn(asn);
    setAppliedDateRange(dateRange);
    setPage(1);
    setShowAdvancedFilters(false);
  };

  const applySearch = (value: string) => {
    const trimmed = value.trim();
    setAppliedAsn((prev) => {
      if (prev === trimmed) return prev;
      setPage(1);
      return trimmed;
    });
  };

  const resetFilters = () => {
    setAsn("");
    setDateRange([]);
    setAppliedAsn("");
    setAppliedDateRange([]);
    setPage(1);
    setPageSize(50);
  };

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];

    if (appliedAsn) {
      chips.push({
        label: `ASN: ${appliedAsn}`,
        onRemove: () => {
          setAsn("");
          setAppliedAsn("");
          setPage(1);
        },
      });
    }

    if (appliedDateRange?.length === 2) {
      chips.push({
        label: `Date: ${moment(appliedDateRange[0]).format(
          "DD/MM/YYYY",
        )} - ${moment(appliedDateRange[1]).format("DD/MM/YYYY")}`,
        onRemove: () => {
          setDateRange([]);
          setAppliedDateRange([]);
          setPage(1);
        },
      });
    }

    return chips;
  }, [appliedAsn, appliedDateRange]);

  const downloadTextFile = (
    content: string,
    filename: string,
    mimeType: string,
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const header = [
      "ASN",
      "Date & Time",
      "Expected QTY",
      "Received QTY",
      "Missing QTY",
      "Unexpected QTY",
    ];
    const rows: Array<Array<string | number>> = [
      header,
      ...tableRows.map((r) => [
        r.asn,
        r.dateTime ? moment(r.dateTime).format("DD MMM YY hh:mm A") : "",
        r.expectedQty,
        r.receivedQty,
        r.missingQty,
        r.unexpectedQty,
      ]),
    ];
    exportRowsToWorkbook(
      `receive-report-${moment().format("YYYY-MM-DD")}.xlsx`,
      "Receive Report",
      rows,
    );
  };

  return (
    <div className="shadow-card h-full flex flex-col min-h-0 rounded-md gap-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
        {isFetching ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
          </>
        ) : (
          <>
            <MetricCard
              label="EXPECTED ITEMS"
              value={metrics.expectedQty}
              accent="default"
              icon={<PackageOpen />}
            />

            <MetricCard
              label="RECEIVED ITEMS"
              value={metrics.receivedQty}
              accent="green"
              icon={<PackageCheck />}
            />

            <MetricCard
              label="MISSING ITEMS"
              value={metrics.missingQty}
              accent="red"
              icon={<PackageX />}
            />

            <MetricCard
              label="UNEXPECTED ITEMS"
              value={metrics.unexpectedQty}
              accent="orange"
              icon={<AlertTriangle />}
            />
          </>
        )}
      </div>
      <div className="mt-3">
        <ReportToolbar
          searchValue={asn}
          onSearchChange={setAsn}
          onDebounceSearch={applySearch}
          onSearchEnter={applySearch}
          searchPlaceholder="Search ASN"
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() =>
            setShowAdvancedFilters((show) => !show)
          }
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          activeFilterCount={activeFilterChips.length}
          onExportExcel={exportExcel}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
            {/* ASN */}
          

            {/* Time Range */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Time Range
              </label>
              <UICalendar
                params={dateRange}
                setParams={setDateRange}
                handleChange={(dates: moment.Moment[]) =>
                  setDateRange(dates || [])
                }
                className="w-full"
                hideArrows={true}
              />
            </div>
          </div>
        </ReportToolbar>
      </div>
      <ReportFilterChips chips={activeFilterChips} onClearAll={resetFilters} />
      {/* <div className="flex h-[calc(100vh-2rem)] gap-4 p-2 overflow-hidden bg-slate-50">
        <ReceiveList
          pagedRows={pagedRows}
          setSelectedAsn={setSelectedAsn}
          currentAsn={selectedAsn?.asn}
          loading={isFetching}
        />
      </div> */}
      <div className="table-container">
        <UIAgGridTable
          columns={columns}
          data={pagedRows}
          loading={isFetching}
          rowHeight={44}
          // onSortChange={handleGridSortChange}
          onRowClick={(record: ReceiveAsnRow) => {
            const params = new URLSearchParams();

            const normalizedAsn = String(record.asn ?? "")
              .trim()
              .toLowerCase();

            const isBlindAsn = !normalizedAsn || normalizedAsn === "blind";

            if (!isBlindAsn) {
              params.set("asn", record.asn);
            }

            if (record.sku) {
              params.set("sku", record.sku);
            }

            if (record.identityId) {
              params.set("identityId", record.identityId);
            }

            if (appliedDateRange?.length) {
              params.set(
                "from",
                moment.utc(appliedDateRange[0]).format("YYYY-MM-DD"),
              );

              params.set(
                "to",
                moment.utc(appliedDateRange[1]).format("YYYY-MM-DD"),
              );
            }

            if (tenantId) {
              params.set("tenantId", tenantId);
            }

            const drilldownPath = `${Page.REPORTS_RECEIVE_DETAILS}?${params.toString()}`;

            const nextLabel = record.asn
              ? `Site Receive (${record.asn})`
              : "Site Receive Report";

            const nextItem = makeTrailItem(nextLabel, drilldownPath);
            const nextTrail = extendTrail(location, nextItem);

            navigate(drilldownPath, {
              state: writeReportTrailState(location.state, nextTrail),
            });
          }}
        />
        <Pagination
          current={page}
          pageSize={pageSize}
          className="rounded-b-md border-t border-slate-200 dark:border-gray-700"
          total={sortedRows.length}
          hideOnSinglePage={false}
          showSizeChanger={true}
          onPageChange={(p) => {
            setPage(p);
          }}
          onPageSizeChange={(newSize) => {
            setPage(1);
            setPageSize(newSize);
          }}
        />
      </div>
    </div>
  );
};

export default ReceiveReport;
